import React from 'react';
import {
  ChartBarIcon,
  UserGroupIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  CheckBadgeIcon,
  ClockIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useTheme } from '../contexts/ThemeContext';

// График нүктесінің белгісі: «күн» кезеңінде сағат, «жыл» кезеңінде ай, қалғанында күн.
const formatBucket = (iso, period) => {
  const d = new Date(iso);
  if (period === 'day') return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  if (period === 'year') return d.toLocaleDateString('ru-RU', { month: 'short', year: '2-digit' });
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
};

const PERIODS = [
  { value: 'day', label: 'День' },
  { value: 'week', label: 'Неделя' },
  { value: 'month', label: 'Месяц' },
  { value: 'year', label: 'Год' },
];

const STATUS_META = {
  active: { label: 'Активные', color: 'bg-emerald-500' },
  deleted: { label: 'Скрытые', color: 'bg-rose-500' },
  archived: { label: 'Архивные', color: 'bg-amber-500' },
  blocked: { label: 'Заблокированные', color: 'bg-gray-500' },
};

const MESSAGE_TYPE_LABELS = {
  text: 'Текст',
  image: 'Изображения',
  file: 'Файлы',
  audio: 'Аудио',
  location: 'Геолокация',
};

const fmt = (n) => Number(n || 0).toLocaleString('ru-RU');

const Card = ({ title, children, className = '' }) => (
  <section className={`rounded-block border border-border-primary bg-bg-card shadow-sm ${className}`}>
    {title && <h3 className="border-b border-border-primary px-5 py-3.5 text-sm font-semibold text-text-primary">{title}</h3>}
    {children}
  </section>
);

const KPI = ({ icon: Icon, label, value, tone }) => (
  <div className="rounded-block border border-border-primary bg-bg-card p-4 shadow-sm">
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-xs font-medium text-text-secondary">{label}</p>
        <p className="mt-1 text-2xl font-bold tabular-nums text-text-primary">{value}</p>
      </div>
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tone}`}>
        <Icon className="h-5 w-5" />
      </span>
    </div>
  </div>
);

const Empty = ({ icon: Icon, text }) => (
  <div className="flex h-full flex-col items-center justify-center py-10 text-center text-text-muted">
    <Icon className="mb-2 h-10 w-10" />
    <p className="text-sm">{text}</p>
  </div>
);

const Bars = ({ entries, total, colorFor }) => (
  <ul className="space-y-3 p-5">
    {entries.map(([key, count, label]) => (
      <li key={key}>
        <div className="mb-1 flex items-center justify-between text-sm">
          <span className="text-text-secondary">{label}</span>
          <span className="tabular-nums text-text-primary">
            <span className="font-semibold">{fmt(count)}</span>
            <span className="ml-2 text-xs text-text-muted">{total ? Math.round((count / total) * 100) : 0}%</span>
          </span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-bg-tertiary">
          <div className={`h-full rounded-full ${colorFor(key)}`} style={{ width: `${total ? (count / total) * 100 : 0}%` }} />
        </div>
      </li>
    ))}
  </ul>
);

const ChatStatistics = ({ statistics, period = 'month', onPeriodChange, onRefresh }) => {
  const { theme } = useTheme();
  const dark = theme === 'dark';

  const toolbar = (
    <div className="flex items-center gap-2">
      <div role="tablist" className="flex rounded-lg border border-border-primary bg-bg-card p-0.5">
        {PERIODS.map((p) => (
          <button
            key={p.value}
            type="button"
            role="tab"
            aria-selected={period === p.value}
            onClick={() => onPeriodChange?.(p.value)}
            className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
              period === p.value ? 'bg-accent-grad text-white' : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={onRefresh}
        title="Обновить"
        aria-label="Обновить"
        className="rounded-lg border border-border-primary bg-bg-card p-1.5 text-text-secondary hover:bg-bg-tertiary"
      >
        <ArrowPathIcon className="h-4 w-4" />
      </button>
    </div>
  );

  if (!statistics) {
    return (
      <div className="space-y-4">
        <div className="flex justify-end">{toolbar}</div>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-[86px] animate-pulse rounded-block border border-border-primary bg-bg-card" />
          ))}
        </div>
        <div className="h-72 animate-pulse rounded-block border border-border-primary bg-bg-card" />
      </div>
    );
  }

  const charts = statistics.charts || {};
  const topUsers = statistics.top_users || [];
  const topChats = statistics.top_chats || [];
  const messageTypes = Object.entries(statistics.message_types || {}).sort((a, b) => b[1] - a[1]);
  const typesTotal = messageTypes.reduce((s, [, c]) => s + c, 0);
  const statusEntries = Object.entries(charts.chat_status_distribution || {});
  const statusTotal = statusEntries.reduce((s, [, c]) => s + c, 0);
  const series = (charts.messages_over_time || []).map((p) => ({ ...p, label: formatBucket(p.date, period) }));
  const hasSeries = series.some((p) => p.count > 0);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">{toolbar}</div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KPI icon={ChatBubbleLeftRightIcon} label="Всего чатов" value={fmt(statistics.total_chats)} tone="bg-indigo-500/15 text-indigo-500" />
        <KPI icon={ChatBubbleOvalLeftEllipsisIcon} label="Всего сообщений" value={fmt(statistics.total_messages)} tone="bg-violet-500/15 text-violet-500" />
        <KPI icon={ClockIcon} label="За последние 24 ч" value={fmt(statistics.recent_messages_24h)} tone="bg-emerald-500/15 text-emerald-500" />
        <KPI
          icon={CheckBadgeIcon}
          label="Прочитано"
          value={`${Number(statistics.read_rate || 0).toFixed(1)}%`}
          tone="bg-sky-500/15 text-sky-500"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card title="Сообщения по времени" className="lg:col-span-2">
          <div className="h-72 px-2 py-4">
            {hasSeries ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={series} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={dark ? '#2a2f36' : '#e5e7eb'} vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9CA3AF' }} interval="preserveStartEnd" minTickGap={12} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    cursor={{ fill: dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }}
                    contentStyle={{
                      background: dark ? '#1c2127' : '#fff',
                      border: `1px solid ${dark ? '#2a2f36' : '#e5e7eb'}`,
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    labelStyle={{ color: dark ? '#e5e7eb' : '#111827' }}
                    formatter={(value) => [fmt(value), 'Сообщений']}
                  />
                  <Bar dataKey="count" fill="#6366F1" radius={[4, 4, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty icon={ChartBarIcon} text="Нет сообщений за выбранный период" />
            )}
          </div>
        </Card>

        <div className="space-y-4">
          <Card title="Статусы чатов">
            {statusEntries.length ? (
              <Bars
                entries={statusEntries.map(([k, c]) => [k, c, STATUS_META[k]?.label || k])}
                total={statusTotal}
                colorFor={(k) => STATUS_META[k]?.color || 'bg-gray-500'}
              />
            ) : (
              <Empty icon={ChartBarIcon} text="Нет данных" />
            )}
          </Card>
          <Card title="Типы сообщений">
            {messageTypes.length ? (
              <Bars
                entries={messageTypes.map(([k, c]) => [k, c, MESSAGE_TYPE_LABELS[k] || k])}
                total={typesTotal}
                colorFor={() => 'bg-accent-grad'}
              />
            ) : (
              <Empty icon={EnvelopeIcon} text="Нет данных" />
            )}
          </Card>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title="Самые активные пользователи">
          {topUsers.length ? (
            <ol className="divide-y divide-border-primary">
              {topUsers.slice(0, 5).map((user, i) => (
                <li key={user.user_id} className="flex items-center gap-3 px-5 py-3">
                  <span className={`w-5 text-center text-sm font-semibold tabular-nums ${i < 3 ? 'text-[#7C3AED]' : 'text-text-muted'}`}>{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-text-primary">{user.name}</p>
                    <p className="text-xs text-text-muted">{fmt(user.chat_count)} чатов · ID {user.user_id}</p>
                  </div>
                  <span className="text-sm font-semibold tabular-nums text-text-primary">{fmt(user.message_count)}</span>
                </li>
              ))}
            </ol>
          ) : (
            <Empty icon={UserGroupIcon} text="Нет данных о пользователях" />
          )}
        </Card>

        <Card title="Самые активные чаты">
          {topChats.length ? (
            <ol className="divide-y divide-border-primary">
              {topChats.slice(0, 5).map((chat, i) => (
                <li key={chat.chat_id} className="flex items-center gap-3 px-5 py-3">
                  <span className={`w-5 text-center text-sm font-semibold tabular-nums ${i < 3 ? 'text-[#7C3AED]' : 'text-text-muted'}`}>{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-text-primary">
                      {chat.user1_name} <span className="text-text-muted">↔</span> {chat.user2_name}
                    </p>
                    <p className="text-xs text-text-muted">
                      #{chat.chat_id}
                      {chat.last_activity && ` · ${new Date(chat.last_activity).toLocaleDateString('ru-RU')}`}
                    </p>
                  </div>
                  <span className="text-sm font-semibold tabular-nums text-text-primary">{fmt(chat.message_count)}</span>
                </li>
              ))}
            </ol>
          ) : (
            <Empty icon={ChatBubbleOvalLeftEllipsisIcon} text="Нет данных о чатах" />
          )}
        </Card>
      </div>
    </div>
  );
};

export default ChatStatistics;
